import { useEffect, useId, useRef, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { listarJogadores } from '../../jogadores/api/jogadores';
import type { Jogador } from '../../jogadores/api/jogadores';
import { JanelaModal } from '../../participantes/ui/JanelaModal';
import { criarGrupo } from '../api/operacao';
import type { GrupoCriado } from '../api/operacao';
import { SITUACAO } from './acoesDaLinha';
import { prenderFoco } from './foco';
import { SeloDeConexao } from '../../participantes/ui/SeloDeConexao';

interface NovoGrupoProps {
    turmaId: string;
    csrfToken: string;
    /** "07": o número que a tela espera; quem decide é o servidor (`DP-RA-042`). */
    numero: string;
    /** Rótulo de cada grupo pelo id ("Grupo 01"), para a coluna "Grupo atual". */
    rotulos: Record<string, string>;
    aoFechar: () => void;
    aoCriar: (grupo: GrupoCriado) => void;
    /** O servidor respondeu `409 grupo_ja_existe`: a Operação relê e o título acompanha. */
    aoConflito: () => void;
}

/**
 * Novo grupo (`RA7`, modelo `docs/img_refac/img_2.png`): seleciona participantes da turma e cria o
 * grupo com todos eles numa requisição só. O grupo nasce em espera no Scan Pixel (`DP-RA-012`).
 */
export function NovoGrupo({ turmaId, csrfToken, numero, rotulos, aoFechar, aoCriar, aoConflito }: NovoGrupoProps) {
    const idDoTitulo = useId();
    const janela = useRef<HTMLDivElement>(null);
    const [jogadores, setJogadores] = useState<Jogador[] | null>(null);
    const [erroCarga, setErroCarga] = useState(false);
    const [pesquisa, setPesquisa] = useState('');
    const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
    const [confirmando, setConfirmando] = useState(false);
    const [processando, setProcessando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const confirmandoAgora = useRef(false);
    confirmandoAgora.current = confirmando;
    const rotulo = `Grupo ${numero}`;

    useEffect(() => {
        let ativo = true;
        listarJogadores(turmaId)
            .then((resposta) => ativo && setJogadores(resposta.jogadores))
            .catch(() => ativo && setErroCarga(true));
        return () => {
            ativo = false;
        };
    }, [turmaId]);

    useEffect(() => {
        janela.current?.focus();
        function aoTeclar(evento: KeyboardEvent) {
            // Com o popup aberto, Esc e Tab são dele (`DP-RA-036`).
            if (confirmandoAgora.current) return;
            if (evento.key === 'Escape') aoFechar();
            if (evento.key === 'Tab' && janela.current) prenderFoco(janela.current, evento);
        }
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aoFechar]);

    const termo = pesquisa.trim().toLowerCase();
    // Quem está sem grupo primeiro, como no modelo; depois por grupo e matrícula.
    const visiveis = (jogadores ?? [])
        .filter((jogador) => !termo || jogador.login.toLowerCase().includes(termo) || (jogador.nome ?? '').toLowerCase().includes(termo))
        .sort((a, b) => (a.grupo_id ?? '').localeCompare(b.grupo_id ?? '') || a.login.localeCompare(b.login));

    function alternar(id: string) {
        setSelecionados((atual) => {
            const proximo = new Set(atual);
            if (proximo.has(id)) proximo.delete(id);
            else proximo.add(id);
            return proximo;
        });
    }

    async function criar() {
        if (processando || selecionados.size === 0) return;
        setProcessando(true);
        setErro(null);
        try {
            const criado = await criarGrupo({ turmaId, participantes: [...selecionados], csrfToken });
            aoCriar(criado);
        } catch (falha) {
            setConfirmando(false);
            if (falha instanceof ErroRespostaHttp && falha.status === 409) {
                setErro('Outro operador criou um grupo com este número antes. A tela foi atualizada: confira e tente de novo.');
                aoConflito();
            } else if (falha instanceof ErroRespostaHttp && typeof falha.corpo?.mensagem === 'string') {
                setErro(falha.corpo.mensagem);
            } else {
                setErro('Não foi possível criar o grupo.');
            }
        } finally {
            setProcessando(false);
        }
    }

    return (
        <div className="fundo-modal-grupo">
            <div className="modal-grupo modal-novo-grupo" role="dialog" aria-modal="true" aria-labelledby={idDoTitulo} tabIndex={-1} ref={janela}>
                <button type="button" className="fechar-modal-grupo" aria-label="Fechar" onClick={aoFechar}>
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M6 6l12 12M18 6 6 18" />
                    </svg>
                </button>
                <h2 id={idDoTitulo}>{rotulo}</h2>
                <h3 className="subtitulo-novo-grupo">Adicionar participantes</h3>
                <label className="pesquisa-modal-grupo">
                    Pesquisar
                    <input
                        type="text"
                        value={pesquisa}
                        placeholder="Pesquise pelo nome ou matrícula"
                        onChange={(evento) => setPesquisa(evento.target.value)}
                    />
                </label>
                {erroCarga && <p className="erro-operacao">Não foi possível carregar os participantes.</p>}
                {erro && <p className="erro-operacao" role="alert">{erro}</p>}
                {jogadores && (
                    <div className="rolagem-novo-grupo">
                        <table className="tabela-modal-grupo" aria-label="Participantes da turma">
                            <thead>
                                <tr>
                                    <th scope="col">{`Seleção (${selecionados.size})`}</th>
                                    <th scope="col">Matrícula</th>
                                    <th scope="col">Nome</th>
                                    <th scope="col">Situação</th>
                                    <th scope="col">Grupo atual</th>
                                    <th scope="col">Conexão</th>
                                    <th scope="col">Tablet</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map((jogador) => (
                                    <tr key={jogador.id}>
                                        <td className="coluna-selecao">
                                            <input
                                                type="checkbox"
                                                aria-label={`Selecionar ${jogador.login}`}
                                                checked={selecionados.has(jogador.id)}
                                                onChange={() => alternar(jogador.id)}
                                            />
                                        </td>
                                        <td className="matricula">{jogador.login}</td>
                                        <td>{jogador.nome ?? '–'}</td>
                                        <td>{SITUACAO[jogador.situacao]}</td>
                                        <td>{jogador.grupo_id ? rotulos[jogador.grupo_id] ?? `Grupo ${jogador.grupo_id}` : 'Sem grupo'}</td>
                                        <td>
                                            <SeloDeConexao conexao={jogador.conexao} />
                                        </td>
                                        <td>{jogador.tablet ?? '–'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {visiveis.length === 0 && <p className="vazio-operacao">Nenhum participante encontrado.</p>}
                    </div>
                )}
                <div className="rodape-novo-grupo">
                    <button type="button" className="botao-secundario" onClick={aoFechar}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="botao-criar-grupo"
                        disabled={selecionados.size === 0 || processando}
                        onClick={() => setConfirmando(true)}
                    >
                        Criar Grupo
                    </button>
                </div>
            </div>
            {confirmando && (
                <JanelaModal titulo={`Criar ${rotulo}?`} aoFechar={() => setConfirmando(false)} bloqueada={processando}>
                    <p>O grupo será criado e iniciará na estação Scan Pixel.</p>
                    <p>
                        Qualquer participante já vinculado(a) a outro grupo será realocado(a) e perderá seu progresso
                        individual no grupo anterior.
                    </p>
                    <div className="botoes-confirmacao">
                        <button type="button" className="botao-secundario" onClick={() => setConfirmando(false)} disabled={processando}>
                            Cancelar
                        </button>
                        <button type="button" className="botao-confirmar" onClick={() => void criar()} disabled={processando}>
                            {processando ? 'Processando…' : 'Criar'}
                        </button>
                    </div>
                </JanelaModal>
            )}
        </div>
    );
}
